import { memo, useCallback, useState } from 'react';
import styled from '@emotion/styled';

import { useCurrentRoute } from '../../../../lib/useCurrentRoute';
import useTracking from '../../../../lib/useTracking';
import zIndices from '../../../../lib/zIndices';
import ScreenReaderOnly from '../../../ScreenReaderOnly';
import MobileNavList from '../Mobile/MobileNavList';

import type { ReactNode } from 'react';
import type { ThemeProps } from '@emotion/react';
import type { CSSObject } from '@emotion/styled';
import type { NavProps as DesktopNavProps } from '../Desktop/Nav';

interface NavProps extends DesktopNavProps {
  children?: ReactNode | undefined;
  showMenuText?: boolean | undefined;
}

interface MobileNavWrapProps {
  open: boolean;
}

const MobileNavWrap = styled("nav")`
  left: 0;
  overflow: hidden;
  position: fixed;
  top: 0;
  transition: all 0.5s;
  width: 100%;
  z-index: ${zIndices.mobileNav};
  background-color: ${(props: ThemeProps): string =>
    props.theme.colors.background};

  ${(props: MobileNavWrapProps): string =>
    props.open ? 'height: 100%;' : 'height: 0;'}

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.Nav?.MobileNav?.Tray};

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileNavListWrap = styled("div")`
  padding-top: 56px;
  height: 100vh;
  overflow-y: auto;
`;

const NavButtonWrap = styled("div")`
  display: block;
  height: 40px;
  left: 8px;
  margin: 0;
  overflow: hidden;
  position: fixed;
  top: 8px;
  width: 40px;
  z-index: ${zIndices.mobileNavButton};

  @media (min-width: 768px) {
    display: none;
  }
`;

const NavBurger = styled("span")`
  ::before,
  ::after {
    content: '';
    height: 3px;
    left: 20%;
    margin: 0 auto;
    border-radius: 0;
    margin-top: -0.15em;
    position: absolute;
    top: 50%;
    transition: all 0.4s;
    width: 24px;

    background: ${({ theme }: ThemeProps): string => theme.fonts.s5.color};

    box-shadow: 0 -10px 0 0 ${({ theme }: ThemeProps): string => theme.fonts.s5.color},
      0 10px 0 0 ${({ theme }: ThemeProps): string => theme.fonts.s5.color};

    ${({ theme }: ThemeProps): CSSObject | undefined =>
      theme.liteSite?.Nav?.MobileNav?.Burger};
  }

  ${(props: MobileNavWrapProps): string =>
    props.open
      ? `
      ::after {
        box-shadow: 0 0 0 0 rgba(0,0,0,0), 0 0 0 0 rgba(0,0,0,0);
        transform: rotateZ(45deg);
      }

      ::before {
        box-shadow: 0 0 0 0 rgba(0,0,0,0), 0 0 0 0 rgba(0,0,0,0);
        transform: rotateZ(-45deg);
      }
    `
      : ''}
`;

const NavButton = styled("button")`
  background: none;
  border: none;
  font-size: 10px;
  height: 40px;
  width: 40px;
  border-radius: 0;

  ${(props: ThemeProps): CSSObject | undefined =>
    props.theme.components.Nav?.Button}
`;

const LiteMobileButtonText = styled("span")`
  display: inline-block;
  position: absolute;
  top: 12px;
  left: 60px;
  font-size: 18px;
  text-transform: uppercase;
  z-index: ${zIndices.mobileNavButton};

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.Nav?.Button}
`;

function MobileNavAndButton({
  pages,
  children,
  showMenuText,
}: NavProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const userDecisionArea = 'mobile nav menu';

  const currentRoute = useCurrentRoute();

  const track = useTracking('guest', currentRoute, userDecisionArea);

  const onClick = useCallback((): void => {
    setMobileNavOpen((prevOpen: boolean): boolean => {
      track(`${!prevOpen ? 'open' : 'close'} menu`);

      return !prevOpen;
    });
  }, [track]);

  return (
    <>
      <NavButtonWrap>
        <NavButton onClick={onClick}>
          <NavBurger open={mobileNavOpen} />

          {showMenuText ? (
            <LiteMobileButtonText>Menu</LiteMobileButtonText>
          ) : (
            <ScreenReaderOnly>Menu Button</ScreenReaderOnly>
          )}
        </NavButton>
      </NavButtonWrap>

      {children}

      <MobileNavWrap open={mobileNavOpen} role="navigation">
        <MobileNavListWrap>
          <MobileNavList
            pages={pages}
            setMobileNavOpen={setMobileNavOpen}
            userDecisionArea={userDecisionArea}
          />
        </MobileNavListWrap>
      </MobileNavWrap>
    </>
  );
}

export default memo(MobileNavAndButton);
